/**
 * Google-Docs-style markdown triggers at the start of a paragraph: typing `- ` or `* ` turns a
 * Standard paragraph into a bullet (Itemize), `1. ` / `1) ` into a numbered item (Enumerate),
 * `# `…`###### ` into headings (# = the class's top heading: Chapter where the class has one,
 * Section otherwise), `! `…`!!!!!! ` into matching unnumbered headings. The typed marker
 * disappears; Backspace right after puts it back
 * (prosemirror-inputrules' undoInputRule, bound in keymap.ts). Only Standard / Plain paragraphs
 * change — a heading or a list item already is what it is.
 *
 * A math symbol typed as TeX in the text (`\alpha `, `\ll,` — the habit of everyone coming from
 * Overleaf, and what lands in the text after Space left a formula LyX-style) becomes an inline
 * formula instead of the literal `\textbackslash alpha` it used to be saved as; Backspace right
 * after gives the typed text back. Raw TeX (ERT, listings, code) is left alone.
 */
import { InputRule, inputRules } from 'prosemirror-inputrules';
import { schema, SYMBOLS } from '@overlyx/core';
import { inRawText } from '../rawtext';
import type { Plugin } from 'prosemirror-state';
import type { LayoutInfo } from '../../api';
import { editorContext } from '../context';

const PLAIN = new Set(['Standard', 'Plain Layout']);
const HEADINGS = ['Chapter', 'Section', 'Subsection', 'Subsubsection', 'Paragraph', 'Subparagraph'];

/** The heading for repeated # (numbered) or ! (unnumbered), at the same depth in this class. */
export function headingForHashes(level: number, layouts?: LayoutInfo[] | null, unnumbered = false): string | null {
  const known = (n: string) => !layouts || !layouts.length || layouts.some(l => l.name === n);
  // Keep both markers at the same depth even if a class lacks one starred counterpart.
  const ladder = HEADINGS.filter(n => known(n) || known(n + '*'));
  const heading = ladder[level - 1];
  const name = heading && heading + (unnumbered ? '*' : '');
  return name && known(name) ? name : null;
}

function layoutRule(re: RegExp, layoutFor: (m: RegExpMatchArray, layouts?: LayoutInfo[] | null) => string | null): InputRule {
  return new InputRule(re, (state, match, start, end) => {
    const $from = state.selection.$from;
    const par = $from.parent;
    if (par.type.name !== 'paragraph' || start !== $from.start() || !PLAIN.has(String(par.attrs.layout)) || inRawText(state)) return null;
    const layouts = editorContext.meta?.layouts;
    const layout = layoutFor(match, layouts);
    if (!layout || (layouts && layouts.length && !layouts.some(l => l.name === layout))) return null;
    return state.tr.delete(start, end).setNodeMarkup($from.before(), undefined, { ...par.attrs, layout });
  });
}

/** `\name` + a space or punctuation, where `name` is a math symbol (Greek letters, relations, operators): an inline formula. */
const mathSymbolRule = new InputRule(/\\([A-Za-z]+)([\s,.;:!?)\]])$/, (state, match, start, end) => {
  const name = match[1];
  if (SYMBOLS[name]?.i !== 'sym' || inRawText(state) || !state.selection.$from.parent.isTextblock) return null;
  const math = schema.nodes.math_inline.create({ latex: '\\' + name, delim: '$' });
  return state.tr.replaceWith(start, end, [math, schema.text(match[2])]);
});

export function markdownRulesPlugin(): Plugin {
  return inputRules({
    rules: [
      layoutRule(/^[-*]\s$/, () => 'Itemize'),
      layoutRule(/^1[.)]\s$/, () => 'Enumerate'),
      layoutRule(/^(#{1,6})\s$/, (m, layouts) => headingForHashes(m[1].length, layouts)),
      layoutRule(/^(!{1,6})\s$/, (m, layouts) => headingForHashes(m[1].length, layouts, true)),
      mathSymbolRule,
    ],
  });
}
